import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import BookingEditDialog from "./BookingEditDialog";

const STATUS_OPTIONS = ["pending", "confirmed", "cancelled", "completed"];
const STATUS_COLOR = {
    pending: "bg-bone2 text-espresso border-espresso/30",
    confirmed: "bg-forest/10 text-forest border-forest/30",
    cancelled: "bg-terracotta/15 text-terracotta border-terracotta/30",
    completed: "bg-espresso/15 text-espresso border-espresso/30",
};

export default function BookingsTab() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(null);
    const refresh = () => api.get("/bookings").then((r) => setItems(r.data));
    useEffect(() => { refresh(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/bookings/${id}/status`, { status });
            toast.success(`Marked ${status}`);
            refresh();
        } catch { toast.error("Update failed"); }
    };

    const setPaid = async (id, payment_status) => {
        try {
            await api.patch(`/bookings/${id}/payment-status`, { payment_status });
            toast.success(payment_status === "paid" ? "Marked paid" : "Marked unpaid");
            refresh();
        } catch { toast.error("Update failed"); }
    };

    const filtered = filter === "all" ? items : items.filter((b) => b.status === filter);

    return (
        <div data-testid="admin-bookings">
            <PageHeader eyebrow="Admin · Reservations" title="Manage all stays." />
            <div className="flex gap-2 mb-6 flex-wrap">
                {["all", ...STATUS_OPTIONS].map((s) => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`bookings-filter-${s}`} className={`px-4 py-2 text-xs uppercase tracking-widest2 border ${filter === s ? "bg-forest text-bone border-forest" : "border-espresso/20 text-espresso hover:bg-bone2"}`}>{s}</button>
                ))}
            </div>
            <div className="border border-espresso/15 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-widest2 text-espresso bg-bone2/50">
                        <tr>
                            <th className="px-4 py-3">Guest</th>
                            <th className="px-4 py-3">Cabin</th>
                            <th className="px-4 py-3">Dates</th>
                            <th className="px-4 py-3">Total</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Payment</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-12 text-center text-muted2">No reservations.</td></tr>
                        )}
                        {filtered.map((b) => (
                            <tr key={b.id} className="border-t border-espresso/10" data-testid={`booking-${b.id}`}>
                                <td className="px-4 py-4">
                                    <div className="font-medium">{b.guest_name}</div>
                                    <div className="text-xs text-muted2">{b.guest_email}</div>
                                </td>
                                <td className="px-4 py-4">{b.cabin_name}</td>
                                <td className="px-4 py-4">{b.check_in} → {b.check_out}<div className="text-xs text-muted2">{b.nights} nt · {b.guests} g</div></td>
                                <td className="px-4 py-4">${b.total_price.toFixed(0)}</td>
                                <td className="px-4 py-4">
                                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest2 border ${STATUS_COLOR[b.status]}`}>{b.status}</span>
                                </td>
                                <td className="px-4 py-4">
                                    <span data-testid={`booking-payment-${b.id}`} className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest2 ${b.payment_status === "paid" ? "bg-forest/10 text-forest" : "bg-terracotta/10 text-terracotta"}`}>
                                        {b.payment_status === "paid" ? "Paid" : "Unpaid"}
                                    </span>
                                </td>
                                <td className="px-4 py-4 text-right">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger data-testid={`booking-actions-${b.id}`} className="p-2 hover:bg-bone2 rounded-sm">
                                            <MoreHorizontal size={16} />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem data-testid={`booking-edit-${b.id}`} onClick={() => setEditing(b)}>Edit reservation</DropdownMenuItem>
                                            {b.payment_status === "paid"
                                                ? <DropdownMenuItem data-testid={`booking-mark-unpaid-${b.id}`} onClick={() => setPaid(b.id, "unpaid")}>Mark unpaid</DropdownMenuItem>
                                                : <DropdownMenuItem data-testid={`booking-mark-paid-${b.id}`} onClick={() => setPaid(b.id, "paid")}>Mark paid</DropdownMenuItem>}
                                            <DropdownMenuSeparator />
                                            {STATUS_OPTIONS.map((s) => (
                                                <DropdownMenuItem key={s} onClick={() => setStatus(b.id, s)}>Mark {s}</DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <BookingEditDialog
                    booking={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); refresh(); }}
                />
            )}
        </div>
    );
}
